import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Loader2 } from 'lucide-react';
import { updateMyPhone } from '@/lib/api';
import { formatNationalAsTyped, parseFromDisplay } from '@/lib/phone-format';
import formStyles from '@/components/Form.module.css';
import NamedIcon from './NamedIcon';
import styles from './PhoneCaptureModal.module.css';

export interface PhoneCaptureModalProps {
  /** Headline copy. Defaults to "Add a phone number." */
  title?: string;
  /** Why we're asking — varies by surface. Plain string or JSX. */
  reason: ReactNode;
  /** Called AFTER the phone is persisted server-side, with the
   *  E.164-formatted value. Use this to chain a follow-up
   *  retry (e.g. resume the registration that hit PHONE_REQUIRED). */
  onCapture: (phone: string) => Promise<void>;
  /** Optional skip handler. Omit to make the modal mandatory. */
  onSkip?: () => void;
}

// Minimal country list — covers the markets EVI v2 actually serves
// today plus a few common neighbours. Extending later is a one-line
// addition. Country code is the +N prefix; all input fields render
// the national number in plain digits.
const COUNTRY_OPTIONS: Array<{ code: string; label: string }> = [
  { code: '+1', label: '🇺🇸 +1 (US/CA)' },
  { code: '+52', label: '🇲🇽 +52 (MX)' },
  { code: '+44', label: '🇬🇧 +44 (UK)' },
  { code: '+61', label: '🇦🇺 +61 (AU)' },
  { code: '+49', label: '🇩🇪 +49 (DE)' },
  { code: '+33', label: '🇫🇷 +33 (FR)' },
  { code: '+34', label: '🇪🇸 +34 (ES)' },
  { code: '+81', label: '🇯🇵 +81 (JP)' },
];

/** Strip everything that's not a digit — call before composing the
 *  E.164 string and before length checks. Re-exports parseFromDisplay
 *  for the NA-formatted path; non-NA inputs hit the same regex via
 *  the digit-strip semantics. */
function digitsOnly(s: string): string {
  return parseFromDisplay(s);
}

/**
 * Phone capture modal. Shown when a resident-tier flow needs the
 * caller's phone but it's not yet on file. Two surfaces today:
 *   - AddChargerPage (owner side, on POST /chargers/resident-register
 *     returning 412 PHONE_REQUIRED)
 *   - MyAccessPage (recipient side, on /my-access render with
 *     account.phone === null and at least one shared charger)
 *
 * Composition: country code dropdown + national number input.
 * Final E.164 string = countryCode + digitsOnly(nationalNumber).
 * Format validation is loose at the form layer (1+ digit, max 15
 * to stay inside the E.164 ceiling); the server enforces the
 * strict regex.
 */
export function PhoneCaptureModal({
  title,
  reason,
  onCapture,
  onSkip,
}: PhoneCaptureModalProps) {
  const { t } = useTranslation('modals');
  const [country, setCountry] = useState('+1');
  const [nationalNumber, setNationalNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const composedDigits = digitsOnly(nationalNumber);
  const composedE164 = `${country}${composedDigits}`;
  const validForSubmit = composedDigits.length >= 7 && composedDigits.length <= 15;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validForSubmit) {
      setErrorMsg(t('phone.errorInvalid'));
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      await updateMyPhone(composedE164);
      await onCapture(composedE164);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('phone.errorSaveFailed'));
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-phone-title">
      <div className={styles.card}>
        <div className={styles.iconWrap} aria-hidden>
          {/* size 72 vs the prior 36 so the phone Lottie dominates
              its slot. The .iconWrap drops its circle bg + border
              (see PhoneCaptureModal.module.css) so the icon reads
              alone instead of competing with a visible backing
              circle. Matches the pattern shipped in 56bbfd0 for
              the coupons empty-state icon; other Lottie slots
              (profile rows, ways-to-earn cards, bottom nav) keep
              their existing styling. */}
          <NamedIcon name="phone" fallback={<Phone size={72} />} size={72} />
        </div>
        <h2 id="evi-phone-title" className={styles.title}>{title ?? t('phone.title')}</h2>
        <p className={styles.reason}>{reason}</p>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {errorMsg && (
            <div className={styles.error} role="alert">{errorMsg}</div>
          )}
          <div className={styles.fieldRow}>
            <label className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>{t('phone.country')}</span>
              <select
                className={`${formStyles.input} ${styles.countrySelect}`}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={submitting}
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className={`${styles.fieldGroup} ${styles.numberGroup}`}>
              <span className={styles.fieldLabel}>{t('phone.phoneNumber')}</span>
              <input
                className={formStyles.input}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                value={nationalNumber}
                onChange={(e) => {
                  // NA path: format to XXX-XXX-XXXX as the user types.
                  // Other countries: pass through unchanged — formatting
                  // conventions vary internationally and the country
                  // dropdown already discloses the +N prefix, so a user
                  // typing a UK national number gets the digits they
                  // typed and that's what composes into E.164.
                  const next = country === '+1'
                    ? formatNationalAsTyped(e.target.value)
                    : e.target.value;
                  setNationalNumber(next);
                }}
                placeholder={country === '+1' ? '555-123-4567' : '5551234567'}
                disabled={submitting}
                required
              />
            </label>
          </div>
          <p className={styles.hint}>
            {t('phone.hint', {
              value: composedDigits ? composedE164 : `${country}…`,
            })}
          </p>

          <div className={styles.actions}>
            {onSkip && (
              <button
                type="button"
                className={styles.skipBtn}
                onClick={onSkip}
                disabled={submitting}
              >
                {t('phone.skip')}
              </button>
            )}
            <button
              type="submit"
              className={styles.saveBtn}
              disabled={submitting || !validForSubmit}
            >
              {submitting
                ? <><Loader2 size={14} className={styles.spin} /> {t('phone.saving')}</>
                : t('phone.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
